import { useEffect, useRef } from 'react';
import { getEcho } from '../lib/echo';

/**
 * O payload chega do socket sem contrato. Cada handler declara o formato que
 * espera e é ele quem garante — `never` no parâmetro deixa qualquer handler
 * específico ser aceito (contravariância) sem recorrer a `any`.
 */
type Handler = (payload: never) => void;
type EventsMap = Record<string, Handler>;
type HandlerCru = (payload: unknown) => void;

interface Options {
    private?: boolean;
}

interface EchoChannel {
    listen: (event: string, callback: HandlerCru) => void;
    stopListening: (event: string) => void;
}

/**
 * Subscreve um canal Echo (por padrão privado) e registra um conjunto de listeners.
 *
 * Os nomes de evento seguem o padrão do broadcastAs() do Laravel: use a string
 * exatamente como retornada por broadcastAs(), sem o ponto inicial — ele é
 * adicionado internamente.
 *
 *   useChannel('titulos.list', {
 *     'titulos.list_changed': payload => { ... },
 *   });
 */
export function useChannel(channel: string | null, events: EventsMap, opts: Options = {}) {
    const isPrivate = opts.private ?? true;
    const handlersRef = useRef(events);
    handlersRef.current = events;

    useEffect(() => {
        if (!channel) return;

        const echo = getEcho();
        const ch = (isPrivate ? echo.private(channel) : echo.channel(channel)) as EchoChannel;

        const boundEvents = Object.keys(handlersRef.current);
        const wrappers: Record<string, HandlerCru> = {};

        boundEvents.forEach((evt) => {
            wrappers[evt] = (payload: unknown) => (handlersRef.current[evt] as HandlerCru | undefined)?.(payload);
            ch.listen(`.${evt}`, wrappers[evt]);
        });

        return () => {
            boundEvents.forEach((evt) => ch.stopListening(`.${evt}`));
            try {
                echo.leave(isPrivate ? `private-${channel}` : channel);
            } catch {
                /* noop */
            }
        };
    }, [channel, isPrivate]);
}
